import { Head, Link } from '@inertiajs/react';
import { MainLayout } from '../../layouts/MainLayout';

interface PackItem {
  name: string;
  quantity_label: string;
  line: string;
  url: string;
  image_url: string | null;
}

interface ExtraGroup {
  label: string;
  multiple: boolean;
  items: PackItem[];
}

interface UpcomingSlot {
  label: string;
  time_range: string;
}

interface ZoneQuote {
  title: string;
  anchor: string;
  minimum_label: string;
  free_label: string;
  cost_lines: string[];
  upcoming: UpcomingSlot[];
  reaches_minimum: boolean;
  shipping_label: string;
  total_label: string | null;
}

interface DeliveryShift {
  label: string;
  range: string;
}

interface Household {
  people: number | null;
  days: number | null;
  per_person_per_day_label: string | null;
  feature: string | null;
  per_person_sentence: string | null;
}

interface FaqItem {
  question: string;
  answer: string;
}

interface RelatedPack {
  title: string;
  url: string;
  image_url: string | null;
  price_label: string;
  description: string | null;
}

interface RecipeLink {
  name: string;
  url: string;
  note: string | null;
}

interface PageLinks {
  pack: string;
  delivery: string;
  returns: string;
  why_us: string;
  produce: string;
  family: string;
  gift: string;
  neighborhoods: string;
  packs: string;
}

interface BolsonPageProps {
  title: string;
  description: string;
  appUrl: string;
  pack: {
    title: string;
    url: string;
    image_url: string | null;
    description: string | null;
    features: string[];
  };
  price_label: string;
  price_is_from: boolean;
  date_label: string;
  intro: string;
  answer_ready: string[];
  base_vegetables: PackItem[];
  base_fruits: PackItem[];
  base_count: number;
  extra_groups: ExtraGroup[];
  extra_count: number;
  household: Household;
  zones: ZoneQuote[];
  shifts: DeliveryShift[];
  ordering_rules: string[];
  holiday_note: string;
  storage_note: string;
  storage_url: string;
  cart_edit: string;
  faqs: FaqItem[];
  recipes: RecipeLink[];
  related_packs: RelatedPack[];
  cooking_packs: RelatedPack[];
  links: PageLinks;
}

function ItemTable({ items, caption }: { items: PackItem[]; caption: string }) {
  if (items.length === 0) return null;

  return (
    <div className="overflow-x-auto rounded-2xl border border-gray-200 bg-white shadow-sm">
      <table className="min-w-full text-left text-sm">
        <caption className="sr-only">{caption}</caption>
        <thead className="bg-gray-50 text-gray-600">
          <tr>
            <th scope="col" className="px-4 py-3 font-semibold">Producto</th>
            <th scope="col" className="px-4 py-3 font-semibold">Cantidad</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.line} className="border-t border-gray-100">
              <th scope="row" className="px-4 py-3 font-semibold text-gray-900">
                <span className="flex items-center gap-3">
                  {/* TODO: reemplazar por fotos reales del bolsón armado. Hoy se usan las imágenes del pack y de cada producto. */}
                  {item.image_url ? (
                    <img src={item.image_url} alt="" className="h-10 w-10 rounded-md object-cover" />
                  ) : null}
                  <a href={item.url} className="underline hover:text-green-800">{item.name}</a>
                </span>
              </th>
              <td className="px-4 py-3 text-gray-700">{item.quantity_label}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function BolsonDeVerdurasPage({
  title,
  description,
  appUrl,
  pack,
  price_label,
  price_is_from,
  date_label,
  intro,
  answer_ready,
  base_vegetables,
  base_fruits,
  base_count,
  extra_groups,
  extra_count,
  household,
  zones,
  shifts,
  ordering_rules,
  holiday_note,
  storage_note,
  storage_url,
  cart_edit,
  faqs,
  recipes,
  related_packs,
  cooking_packs,
  links,
}: BolsonPageProps) {
  const canonical = `${appUrl}/bolson-de-verduras`;
  const splitProduce = base_fruits.length > 0 && base_vegetables.length > 0;
  const datedZones = zones.filter((zone) => zone.upcoming.length > 0);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <MainLayout>
      <Head>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={canonical} />
        {faqs.length > 0 && (
          <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        )}
      </Head>

      <div className="bg-gradient-to-b from-slate-50 to-white pb-16 pt-8 md:pt-12">
        <div className="mx-auto max-w-6xl px-4">
          <header className="mb-10 max-w-3xl">
            <h1 className="mb-3 text-3xl font-bold text-gray-900 md:text-4xl">
              Bolsón de verduras a domicilio en Córdoba
            </h1>
            <p className="text-lg leading-relaxed text-gray-600">{intro}</p>
            {pack.features.length > 0 && (
              <ul className="mt-4 list-disc space-y-1 pl-5 text-gray-700">
                {pack.features.map((feature) => (
                  <li key={feature}>{feature}</li>
                ))}
              </ul>
            )}
            <div className="mt-5 flex flex-wrap gap-3">
              <Link
                href={links.pack}
                className="inline-flex items-center justify-center rounded-lg bg-green-600 px-5 py-3 text-sm font-semibold text-white hover:bg-green-700"
              >
                Pedí tu bolsón
              </Link>
              <Link
                href={links.family}
                className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-5 py-3 text-sm font-semibold text-gray-800 hover:bg-gray-50"
              >
                Si se lo comprás a un familiar
              </Link>
            </div>
          </header>

          <section className="mb-14 max-w-3xl" aria-label="Respuestas directas">
            {answer_ready.map((paragraph) => (
              <p key={paragraph} className="mb-4 leading-relaxed text-gray-800">{paragraph}</p>
            ))}
          </section>

          <section className="mb-14" aria-labelledby="que-trae">
            <h2 id="que-trae" className="mb-3 text-2xl font-bold text-gray-900">
              ¿Qué trae el bolsón familiar?
            </h2>
            <p className="mb-5 max-w-3xl leading-relaxed text-gray-700">{faqs[1]?.answer}</p>
            <p className="mb-5 text-sm text-gray-500">
              {base_count} productos incluidos
              {extra_count > 0 ? ` y ${extra_count} extras opcionales` : ''}. La lista es la selección cargada hoy
              ({date_label}). Cuando cambia la temporada, cambian estos productos.
            </p>

            {splitProduce ? (
              <div className="space-y-8">
                <div>
                  <h3 className="mb-3 text-lg font-semibold text-gray-900">Verduras base</h3>
                  <ItemTable items={base_vegetables} caption="Verduras incluidas en el bolsón, con cantidad" />
                </div>
                <div>
                  <h3 className="mb-3 text-lg font-semibold text-gray-900">Frutas</h3>
                  <ItemTable items={base_fruits} caption="Frutas incluidas en el bolsón, con cantidad" />
                </div>
              </div>
            ) : (
              <ItemTable
                items={[...base_vegetables, ...base_fruits]}
                caption="Productos incluidos en el bolsón, con cantidad"
              />
            )}

            {extra_groups.map((group) => (
              <div key={group.label} className="mt-8">
                <h3 className="mb-2 text-lg font-semibold text-gray-900">{group.label}</h3>
                <p className="mb-3 text-gray-600">
                  {group.multiple
                    ? 'Son opcionales: podés sumar varios. Cada uno cambia el precio.'
                    : 'Es opcional: elegís uno. Cambia el precio.'}
                </p>
                <ItemTable items={group.items} caption={`${group.label}, con cantidad`} />
              </div>
            ))}

            {pack.image_url && (
              <figure className="mt-8 max-w-md">
                {/* TODO: fotos reales del bolsón armado y de su contenido sobre la mesa. La imagen actual es la del pack. */}
                <img
                  src={pack.image_url}
                  alt={pack.title}
                  className="w-full rounded-2xl border border-gray-200 object-cover"
                />
                <figcaption className="mt-2 text-sm text-gray-500">
                  Las imágenes son ilustrativas y generadas con IA. Los productos y el packaging pueden variar.
                </figcaption>
              </figure>
            )}
          </section>

          <section className="mb-14" aria-labelledby="cuanto-cuesta">
            <h2 id="cuanto-cuesta" className="mb-3 text-2xl font-bold text-gray-900">
              ¿Cuánto cuesta? (precio a la fecha)
            </h2>
            <p className="mb-4 max-w-3xl text-3xl font-bold text-green-700">
              {price_is_from && <span className="mr-2 text-lg font-normal text-gray-500">Desde</span>}
              {price_label}
            </p>
            <p className="mb-4 max-w-3xl leading-relaxed text-gray-700">{faqs[0]?.answer}</p>
            <p className="max-w-3xl text-sm text-gray-500">
              Precio a la fecha ({date_label}). Los precios cambian; el vigente es el del carrito, en la ficha del pack.
            </p>

            {household.per_person_sentence && (
              <div className="mt-6 max-w-3xl">
                <h3 className="mb-2 text-lg font-semibold text-gray-900">Cuánto sale por persona y por día</h3>
                <p className="leading-relaxed text-gray-700">{household.per_person_sentence}</p>
              </div>
            )}
          </section>

          <section className="mb-14" aria-labelledby="para-cuantos">
            <h2 id="para-cuantos" className="mb-3 text-2xl font-bold text-gray-900">
              ¿Para cuántas personas alcanza y cuánto dura?
            </h2>
            <p className="mb-4 max-w-3xl leading-relaxed text-gray-700">{faqs[2]?.answer}</p>
            {household.feature && (
              <p className="max-w-3xl text-gray-600">La ficha lo dice así: «{household.feature}».</p>
            )}

            <div className="mt-6 max-w-3xl">
              <h3 className="mb-2 text-lg font-semibold text-gray-900">Qué consumir primero</h3>
              <p className="leading-relaxed text-gray-700">{storage_note}</p>
              <p className="mt-2 text-sm">
                <a href={storage_url} className="font-semibold text-green-700 underline hover:text-green-800">
                  Guía de almacenamiento del Postharvest Center (UC Davis)
                </a>
              </p>
            </div>
          </section>

          <section className="mb-14" aria-labelledby="envio">
            <h2 id="envio" className="mb-3 text-2xl font-bold text-gray-900">
              Envío: zonas, costo y días de entrega
            </h2>
            <p className="mb-5 max-w-3xl leading-relaxed text-gray-700">{faqs[3]?.answer}</p>
            <p className="mb-5 max-w-3xl text-gray-600">
              Entregamos nosotros. El total puesto en tu casa es el precio del bolsón más el envío de esa zona para ese monto.
            </p>

            {zones.length === 0 ? (
              <p className="rounded-2xl border border-gray-200 bg-white p-6 text-gray-600">
                No hay zonas de entrega cargadas.
              </p>
            ) : (
              <div className="overflow-x-auto rounded-2xl border border-gray-200 bg-white shadow-sm">
                <table className="min-w-full text-left text-sm">
                  <caption className="sr-only">
                    Total del bolsón puesto en casa por zona, con el costo de envío de esa zona
                  </caption>
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      <th scope="col" className="px-4 py-3 font-semibold">Zona</th>
                      <th scope="col" className="px-4 py-3 font-semibold">Envío de este bolsón</th>
                      <th scope="col" className="px-4 py-3 font-semibold">Total puesto en tu casa</th>
                      <th scope="col" className="px-4 py-3 font-semibold">Pedido mínimo</th>
                      <th scope="col" className="px-4 py-3 font-semibold">Envío gratis desde</th>
                    </tr>
                  </thead>
                  <tbody>
                    {zones.map((zone) => (
                      <tr key={zone.anchor} className="border-t border-gray-100 align-top">
                        <th scope="row" className="px-4 py-3 font-semibold text-gray-900">{zone.title}</th>
                        <td className="px-4 py-3 text-gray-700">{zone.shipping_label}</td>
                        <td className="px-4 py-3 font-semibold text-gray-900">{zone.total_label ?? '—'}</td>
                        <td className="px-4 py-3 text-gray-700">{zone.minimum_label}</td>
                        <td className="px-4 py-3 text-gray-700">{zone.free_label}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <p className="mt-3 text-sm text-gray-500">
              Montos a la fecha ({date_label}), en pesos. El envío se calcula con el precio del bolsón y los tramos de cada zona.
            </p>
            <p className="mt-2 text-sm">
              <Link href={links.delivery} className="font-semibold text-green-700 underline hover:text-green-800">
                Zonas, tramos y cómo comprobar tu dirección
              </Link>
            </p>

            <h3 className="mb-2 mt-8 text-lg font-semibold text-gray-900">Qué días y en qué horarios</h3>
            <p className="mb-4 max-w-3xl leading-relaxed text-gray-700">{faqs[4]?.answer}</p>
            {shifts.length > 0 && (
              <ul className="mb-4 space-y-1 text-gray-700">
                {shifts.map((shift) => (
                  <li key={shift.range}>
                    {shift.label ? `${shift.label}: ` : ''}
                    {shift.range}
                  </li>
                ))}
              </ul>
            )}
            {ordering_rules.length > 0 && (
              <div className="max-w-3xl">
                <h3 className="mb-2 text-lg font-semibold text-gray-900">Hasta qué hora podés pedir</h3>
                <ul className="list-disc space-y-2 pl-5 text-gray-700">
                  {ordering_rules.map((rule) => (
                    <li key={rule}>{rule}</li>
                  ))}
                </ul>
                {holiday_note && <p className="mt-3 text-gray-700">{holiday_note}</p>}
              </div>
            )}
            {datedZones.length > 0 && (
              <div className="mt-6 grid gap-4 md:grid-cols-2">
                {datedZones.map((zone) => (
                  <article key={zone.anchor} className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                    <h3 className="mb-2 text-base font-semibold text-gray-900">{zone.title}</h3>
                    <ul className="space-y-1 text-sm text-gray-700">
                      {zone.upcoming.map((slot) => (
                        <li key={`${slot.label}-${slot.time_range}`}>
                          <span className="capitalize">{slot.label}</span>
                          <span className="text-gray-400"> · </span>
                          <span className="font-medium text-green-700">{slot.time_range}</span>
                        </li>
                      ))}
                    </ul>
                  </article>
                ))}
              </div>
            )}
          </section>

          <section className="mb-14 max-w-3xl" aria-labelledby="a-tu-gusto">
            <h2 id="a-tu-gusto" className="mb-3 text-2xl font-bold text-gray-900">
              Armalo a tu gusto
            </h2>
            <p className="leading-relaxed text-gray-700">{faqs[5]?.answer}</p>
            <p className="mt-3 text-gray-700">{cart_edit}</p>
          </section>

          <section className="mb-14 max-w-3xl" aria-labelledby="si-llega-mal">
            <h2 id="si-llega-mal" className="mb-3 text-2xl font-bold text-gray-900">
              Si algo llega mal, te devolvemos la plata
            </h2>
            <p className="leading-relaxed text-gray-700">{faqs[6]?.answer}</p>
            <p className="mt-3 text-sm">
              <Link href={links.returns} className="font-semibold text-green-700 underline hover:text-green-800">
                Política de devolución
              </Link>
            </p>
          </section>

          {(recipes.length > 0 || cooking_packs.length > 0) && (
            <section className="mb-14" aria-labelledby="que-cocinar">
              <h2 id="que-cocinar" className="mb-3 text-2xl font-bold text-gray-900">
                Qué cocinar con el bolsón
              </h2>
              <ul className="max-w-3xl space-y-3 text-gray-700">
                {recipes.map((recipe) => (
                  <li key={recipe.url}>
                    <Link href={recipe.url} className="font-semibold text-green-700 underline hover:text-green-800">
                      {recipe.name}
                    </Link>
                    {recipe.note ? <span> {recipe.note}</span> : null}
                  </li>
                ))}
                {cooking_packs.map((item) => (
                  <li key={item.url}>
                    <Link href={item.url} className="font-semibold text-green-700 underline hover:text-green-800">
                      {item.title}
                    </Link>
                    <span> {item.price_label}, a la fecha.</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {related_packs.length > 0 && (
            <section className="mb-14" aria-labelledby="otros-packs">
              <h2 id="otros-packs" className="mb-4 text-2xl font-bold text-gray-900">
                Otros packs que combinan
              </h2>
              <div className="grid gap-4 sm:grid-cols-2">
                {related_packs.map((item) => (
                  <Link
                    key={item.url}
                    href={item.url}
                    className="rounded-2xl border border-gray-200 bg-white p-5 no-underline shadow-sm hover:border-green-300"
                  >
                    {item.image_url && (
                      <img src={item.image_url} alt="" className="mb-3 h-32 w-full rounded-xl object-cover" />
                    )}
                    <h3 className="text-lg font-semibold text-gray-900">{item.title}</h3>
                    <p className="mt-1 font-semibold text-green-700">{item.price_label}</p>
                    {item.description && (
                      <p className="mt-2 line-clamp-3 text-sm text-gray-600">{item.description}</p>
                    )}
                  </Link>
                ))}
              </div>
            </section>
          )}

          <section className="mb-14" aria-labelledby="preguntas">
            <h2 id="preguntas" className="mb-6 text-2xl font-bold text-gray-900">
              Preguntas frecuentes
            </h2>
            <div className="max-w-3xl space-y-6">
              {faqs.map((faq) => (
                <div key={faq.question}>
                  <h3 className="text-lg font-semibold text-gray-900">{faq.question}</h3>
                  <p className="mt-2 leading-relaxed text-gray-700">{faq.answer}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="max-w-3xl" aria-label="Cierre">
            <div className="flex flex-wrap gap-3">
              <Link
                href={links.pack}
                className="inline-flex items-center justify-center rounded-lg bg-green-600 px-5 py-3 text-sm font-semibold text-white hover:bg-green-700"
              >
                Pedí tu bolsón
              </Link>
              <Link
                href={links.family}
                className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-5 py-3 text-sm font-semibold text-gray-800 hover:bg-gray-50"
              >
                Comprarlo para un familiar
              </Link>
            </div>
            <ul className="mt-6 space-y-2 text-sm text-gray-700">
              <li><Link href={links.packs} className="font-semibold text-green-700 underline hover:text-green-800">Todos los packs</Link></li>
              <li><Link href={links.produce} className="font-semibold text-green-700 underline hover:text-green-800">precios de hoy por producto</Link></li>
              <li><Link href={links.delivery} className="font-semibold text-green-700 underline hover:text-green-800">Envíos</Link></li>
              <li><Link href={links.why_us} className="font-semibold text-green-700 underline hover:text-green-800">Por qué elegirnos</Link></li>
              <li><Link href={links.neighborhoods} className="font-semibold text-green-700 underline hover:text-green-800">Barrios privados</Link></li>
              <li><Link href={links.gift} className="font-semibold text-green-700 underline hover:text-green-800">Regalar</Link></li>
              <li><Link href={links.returns} className="font-semibold text-green-700 underline hover:text-green-800">Devoluciones</Link></li>
            </ul>
          </section>
        </div>
      </div>
    </MainLayout>
  );
}
